import React, { useEffect, useState } from 'react';
import { App, Button, Drawer, Form, Input, Spin, Switch, Table } from 'antd';
import Search from 'antd/es/input/Search';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { FormFooter } from '../../../../common/components/FormFooter/FormFooter';
import { ConfirmDelete } from '../../../../common/components/ConfirmDelete/ConfirmDelete';
import { authService } from '../../../../api/authService/authService';
import { FieldType } from '../../interfaces/interfaces';
import {
  getColumns,
  notificationMessages,
  showNotification,
  validateMessages,
} from '../../helpers/helpers';
import StorageService from '../../../../common/helpers/storageService/storage-service';
import { localStorage } from '../../../../../types/types';
import { useAppDispatch } from '../../../../core/redux';
import { authorizationSlice } from '../../../authorization/AuthorizationSlice';

import s from './UsersTable.module.scss';
import { getErrors, getInitData } from '../../../../helpers/helpers';

const initialUserData: FieldType = {
  username: '',
  email: '',
  password: '',
  is_active: false,
  id: 0,
};

const { setIsAuth } = authorizationSlice.actions;

export const UsersTable = () => {
  const dispatch = useAppDispatch();
  const { notification } = App.useApp();

  const {
    data,
    isError: dataIsError,
    error: dataError,
    status: dataStatus,
    isLoading: dataIsLoading,
  } = authService.useGetAllUsersQuery({ skip: 0, limit: 1000 });

  const [
    addUser,
    { isError: isAddErr, error: addError, status: addStatus, isLoading: addIsLoading },
  ] = authService.useAddUserMutation();
  const [
    editUser,
    { isError: isEditErr, error: editError, status: editStatus, isLoading: editIsLoading },
  ] = authService.useEditUserMutation();
  const [
    deleteUser,
    { isError: isDeleteErr, error: deleteError, status: deleteStatus, isLoading: deleteIsLoading },
  ] = authService.useDeleteUserMutation();

  const [editableData, setEditableData] = useState(getInitData(data));
  const [form] = Form.useForm();
  const [isOpenDrawer, setOpenDrawer] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [searched, setSearched] = useState('');

  const showDrawer = (id: number) => {
    const curEditableUser = (data || []).find((user) => user.id === id);
    if (curEditableUser) {
      setIsEdit(false);
      form.setFieldsValue({ ...curEditableUser, is_active: !curEditableUser.is_active });
    } else {
      setIsEdit(true);
      form.setFieldsValue(initialUserData);
    }
    setOpenDrawer(true);
  };

  const onClose = () => {
    setOpenDrawer(false);
  };
  const onSearch = (e) => {
    setSearched(e.target.value);
  };
  const onFinish = (e) => {
    if (e.id) {
      const currentUser = editableData.find(({ id }) => id === e.id);
      editUser({ ...currentUser, ...e, is_active: !e.is_active, password: 'admin' });
    } else {
      addUser({ ...e });
    }
    setOpenDrawer(!isOpenDrawer);
  };
  const onFinishFailed = (e) => {
    const errorFields = e.errorFields.map(({ name }) => name[0]);
    showNotification(notification, 'Заполните обязательные поля', errorFields.join(', '), 'error');
  };

  const handleChangeBlocked = (id, is_active) => {
    const currentUser = editableData.find((user) => user.id === id);
    editUser({ ...currentUser, id, is_active, password: 'admin' });
  };
  const handleDelete = (id) => {
    deleteUser(id);
    setOpenDrawer(false);
  };

  const renderEditUser = () => {
    return (
      <Form
        form={form}
        className={s.addUserForm}
        name='basic'
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        validateMessages={validateMessages}
        layout='vertical'
      >
        <div className={s.formContent}>
          <Form.Item<FieldType> label='Имя' name='username' rules={[{ required: true }]}>
            <Input placeholder='Имя' />
          </Form.Item>
          <Form.Item<FieldType>
            label='Эл.адрес'
            name='email'
            rules={[{ required: true, type: 'email' }]}
          >
            <Input placeholder='Эл.адрес' />
          </Form.Item>
          <Form.Item<FieldType>
            valuePropName='checked'
            label={`${isEdit ? 'Доступ заблокирован' : 'Заблокирован'}`}
            name='is_active'
          >
            <Switch />
          </Form.Item>
          <Form.Item<FieldType> style={{ display: 'none' }} name='id'>
            <input />
          </Form.Item>

          {isEdit ? (
            <Form.Item<FieldType> label='Пароль' name='password' rules={[{ required: true }]}>
              <Input placeholder='Пароль' />
            </Form.Item>
          ) : (
            ''
          )}
        </div>

        <div style={{ height: 120 }}>
          {!isEdit ? (
            <ConfirmDelete
              handleDelete={() => handleDelete(form.getFieldsValue()?.id)}
              placement={'top'}
            >
              <Button style={{ width: 206 }} icon={<DeleteOutlined />} danger>
                Удалить пользователя
              </Button>
            </ConfirmDelete>
          ) : (
            ''
          )}

          <FormFooter />
        </div>
      </Form>
    );
  };

  const filteredData = editableData
    ?.filter((item) => item?.username?.includes(searched))
    .sort((a, b) => a.id - b.id);
  const columns = getColumns(showDrawer, handleChangeBlocked, handleDelete);

  const {
    errorEditData,
    errorDeleteData,
    errorDeleteDetails,
    errorDataDetails,
    errorEditDetails,
    errorAddDetails,
  } = getErrors({ editError, deleteError, dataError, addError });

  useEffect(() => {
    if (
      !editIsLoading &&
      !dataIsLoading &&
      (editStatus === 'fulfilled' || dataStatus === 'fulfilled') &&
      data
    ) {
      setEditableData(getInitData(data || []));
    }
  }, [editIsLoading, dataIsLoading, editStatus, dataStatus, data]);
  useEffect(() => {
    if (errorDataDetails === 'Unauthorized') {
      StorageService.set(localStorage.token, false);
      dispatch(setIsAuth(false));
    }
    if (dataIsError && dataStatus === 'rejected' && !dataIsLoading) {
      showNotification(
        notification,
        errorDataDetails || ' ',
        notificationMessages.errGetUsers,
        'error',
      );
    }
  }, [dataStatus, dataError, dataIsLoading]);
  useEffect(() => {
    if (errorEditDetails === 'Unauthorized') {
      StorageService.set(localStorage.token, false);
      dispatch(setIsAuth(false));
    }
    if (isEditErr && editStatus === 'rejected' && !editIsLoading) {
      showNotification(notification, errorEditData, notificationMessages.errChangeUsers, 'error');
    } else if (editStatus === 'fulfilled') {
      showNotification(notification, notificationMessages.successSave, ' ', 'success');
    }
  }, [isEditErr, editStatus, editError, editIsLoading]);
  useEffect(() => {
    if (errorDeleteDetails === 'Unauthorized') {
      StorageService.set(localStorage.token, false);
      dispatch(setIsAuth(false));
    }
    if (isDeleteErr && deleteStatus === 'rejected' && !deleteIsLoading) {
      showNotification(notification, errorDeleteData, notificationMessages.errDeleteUser, 'error');
    } else if (deleteStatus === 'fulfilled') {
      showNotification(
        notification,
        notificationMessages.successDeleted,
        notificationMessages.successDeletedDescription,
        'success',
      );
    }
  }, [isDeleteErr, deleteStatus, deleteError, deleteIsLoading]);
  useEffect(() => {
    if (errorAddDetails === 'Unauthorized') {
      StorageService.set(localStorage.token, false);
      dispatch(setIsAuth(false));
    }
    if (isAddErr && addStatus === 'rejected' && !addIsLoading) {
      showNotification(
        notification,
        errorAddDetails || ' ',
        notificationMessages.errAddUser,
        'error',
      );
    } else if (addStatus === 'fulfilled') {
      showNotification(notification, notificationMessages.successAddUser, ' ', 'success');
    }
  }, [isAddErr, addStatus, addError, addIsLoading]);

  return (
    <div className={s.UserTable}>
      <Spin
        tip='Loading...'
        spinning={dataIsLoading || editIsLoading || deleteIsLoading}
        size='large'
      >
        <Drawer
          bodyStyle={{ padding: '24px 24px 0 24px' }}
          title={`${isEdit ? 'Создать' : 'Редактировать'} сотрудника`}
          placement='right'
          onClose={onClose}
          open={isOpenDrawer}
        >
          {renderEditUser()}
        </Drawer>

        <div className={s.header}>
          <Search placeholder='Найти' allowClear onChange={onSearch} style={{ width: 300 }} />

          <Button onClick={() => showDrawer(null)} type={'primary'} icon={<PlusOutlined />}>
            Создать
          </Button>
        </div>
        <Table className={s.tableAllProjects} columns={columns} dataSource={filteredData} />
      </Spin>
    </div>
  );
};
